import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';

import SizeError from '@components/advanced_download/options/size_error';
import DownloadButton from '@components/advanced_download/download_button';
import CropDimensionsInputs from '@components/advanced_download/options/crop_dimensions_inputs';
import Filetypes from '@components/advanced_download/options/filetypes';
import {
  cleanExtension,
  defaultAttachment,
  defaultCroppedData,
  socialMediaOptions
} from '@components/advanced_download/helper';

import '@components/advanced_download/options/styles/main';

const Options = (props) => {
  const {
    aspectRatio,
    attachment,
    availablePresets,
    croppedData,
    filetypes,
    fixedDimensions,
    imageToThumbnailRatio,
    selectedFiletype,
    selectedPreset,
    setFixedDimensions,
    setAspectRatio,
    setSetter,
    setSelectedFiletype,
    setSelectedPreset,
    zoomRatio,
    restrictOptionsType,
    handleDownloadLink
  } = props;

  const categories = restrictOptionsType || ['Freeform', 'Social Media', 'Custom'];
  const [selectedCategory, setSelectedCategory] = useState({ label: categories[0], value: categories[0] });
  const [selectedSocialType, setSelectedSocialType] = useState(null);
  const [presetOptions, setPresetOptions] = useState(null);
  const [noCustomPresets, setNoCustomPresets] = useState(false);

  const buildOption = (label, value) => ({ label, value });
  const categoryOptions = () => categories.map((item) => buildOption(item, item));

  const buildPresetOptions = (selectedSocialTypeArgument) => {
    const socialMediaOptionsString = socialMediaOptions.map((option) => option.value).join();
    const displayedPresets = availablePresets.filter((preset) => {
      if (selectedSocialTypeArgument.value === 'Custom') {
        return !socialMediaOptionsString.includes(preset.id.split("_")[0]);
      }
      return preset.id.includes(selectedSocialTypeArgument.value);
    });

    if (displayedPresets.length) {
      return displayedPresets.map((filteredPreset) => (
        buildOption(filteredPreset.name, filteredPreset)
      ));
    }

    return null;
  };

  const onSelectPreset = (selection) => {
    let newAspectRatio = null;
    let newFixedDimensions = false;
    let newFiletype = (attachment && attachment.attributes.extension) || 'PNG';

    if (selection) {
      const { aspect_ratio, format, height, width } = selection.value;
      newFixedDimensions = Boolean(height && width);
      newFiletype = format || 'PNG';

      if (aspect_ratio || newFixedDimensions) {
        newAspectRatio = aspect_ratio || (width / height);
      }
    }

    // batch updates together to avoid rerender
    setSelectedPreset(selection);
    setAspectRatio(newAspectRatio);
    setFixedDimensions(newFixedDimensions);
    setSelectedFiletype(cleanExtension(newFiletype));
  };

  const onSocialTypeChange = (selection) => {
    const newOptions = buildPresetOptions(selection);
    onSelectPreset(newOptions[0]);
    setPresetOptions(newOptions);
    setSelectedSocialType(selection);
  };

  const onSelectedCategoryChange = (selection) => {
    onSelectPreset(null);
    setSelectedCategory(selection);
    setSelectedSocialType(null);
    setPresetOptions(buildPresetOptions(selection));
  };

  useEffect(() => {
    if (restrictOptionsType && restrictOptionsType.includes("Custom")) {
      onSelectedCategoryChange(buildOption("Custom", "Custom"));
    }
  }, [restrictOptionsType]);

  useEffect(() => {
    if (selectedCategory.label === 'Social Media') {
      onSocialTypeChange(socialMediaOptions[0]);
    }

    if (presetOptions) {
      onSelectPreset(presetOptions[0]);
    }

    setNoCustomPresets(selectedCategory.label === 'Custom' && !presetOptions);
  }, [selectedCategory]);

  return (
    <section className="options">
      <div className="options__content">
        <h4 className="title-copy options__content--container">{t('Export Options')}</h4>
        <div className="selections-container options__content--container">
          <label className="bf-label">{t('Crop Type')}</label>
          <Select
            name="select-preset"
            className="options-dropdown"
            value={selectedCategory}
            options={categoryOptions()}
            clearable={false}
            placeholder="Select preset category"
            onChange={onSelectedCategoryChange}
          />
          {selectedCategory && selectedCategory.label === 'Social Media' && (
            <React.Fragment>
              <label className="bf-label">{t('Social Media')}</label>
              <Select
                name="select-social-type"
                className="options-dropdown"
                value={selectedSocialType}
                options={socialMediaOptions}
                clearable={false}
                placeholder="Select preset type"
                onChange={onSocialTypeChange}
              />
            </React.Fragment>
          )}
          {presetOptions && (
            <React.Fragment>
              <label className="bf-label">{t('Image Size')}</label>
              <Select
                name="select-custom-preset"
                className="options-dropdown"
                value={selectedPreset}
                options={presetOptions}
                clearable={false}
                placeholder="Select preset name"
                onChange={onSelectPreset}
              />
            </React.Fragment>
          )}
        </div>
        {noCustomPresets && (
          <div className="no-custom-presets options__content--container">
            <h4>{t('No presets found.')}</h4>
            <p>{t('With custom presets, admins in your org can specify pre-determined crop sizes and aspect ratios. After presets have been created, they will appear here.')}</p>
          </div>
        )}
        {(selectedPreset || selectedCategory.value === 'Freeform') && (
          <div className="dimensions-inputs-container options__content--container">
            <CropDimensionsInputs
              aspectRatio={aspectRatio}
              croppedData={croppedData}
              fixedDimensions={fixedDimensions}
              imageToThumbnailRatio={imageToThumbnailRatio}
              selectedPreset={selectedPreset}
              setAspectRatio={setAspectRatio}
              setSetter={setSetter}
            />
          </div>
        )}
        <Filetypes
          filetypes={filetypes}
          selectedCategory={selectedCategory}
          selectedFiletype={selectedFiletype}
          selectedPreset={selectedPreset}
          setSelectedFiletype={setSelectedFiletype}
        />
        {selectedPreset && (
          <SizeError
            attachment={attachment}
            croppedData={croppedData}
            fixedDimensions={fixedDimensions}
            imageToThumbnailRatio={imageToThumbnailRatio}
            selectedPreset={selectedPreset}
            zoomRatio={zoomRatio}
          />
        )}
      </div>
      <div className="options__buttons">
        <DownloadButton
          attachment={attachment}
          croppedData={croppedData}
          fixedDimensions={fixedDimensions}
          imageToThumbnailRatio={imageToThumbnailRatio}
          selectedFiletype={selectedFiletype}
          selectedPreset={selectedPreset}
          zoomRatio={zoomRatio}
          handleDownloadLink={handleDownloadLink}
        />
      </div>
    </section>
  );
};

Options.propTypes = {
  aspectRatio: PropTypes.number,
  attachment: PropTypes.shape({
    attributes: PropTypes.shape({
      filename: PropTypes.string,
      height: PropTypes.number,
      size: PropTypes.number,
      width: PropTypes.number,
    })
  }),
  availablePresets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    aspect_ratio: PropTypes.number,
    height: PropTypes.number,
    width: PropTypes.number,
    format: PropTypes.string,
    pad: PropTypes.bool,
    crop_gravity: PropTypes.string,
  })),
  croppedData: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  filetypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  fixedDimensions: PropTypes.bool.isRequired,
  imageToThumbnailRatio: PropTypes.number.isRequired,
  selectedFiletype: PropTypes.string.isRequired,
  selectedPreset: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.shape({}),
  }),
  setAspectRatio: PropTypes.func.isRequired,
  setFixedDimensions: PropTypes.func.isRequired,
  setSetter: PropTypes.func.isRequired,
  setSelectedFiletype: PropTypes.func.isRequired,
  setSelectedPreset: PropTypes.func.isRequired,
  zoomRatio: PropTypes.number.isRequired,
  restrictOptionsType: PropTypes.arrayOf(PropTypes.string),
  handleDownloadLink: PropTypes.func,
};

Options.defaultProps = {
  aspectRatio: null,
  attachment: defaultAttachment,
  availablePresets: [{
    id: '',
    name: '',
    aspect_ratio: null,
    height: null,
    width: null,
    format: '',
    pad: false,
    crop_gravity: '',
  }],
  croppedData: defaultCroppedData,
  selectedPreset: null,
  restrictOptionsType: null,
  handleDownloadLink: PropTypes.func,
};

export default Options;
